import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { apiEndpoints } from '../api/client';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from './ui/Toast';

const AccountSettingsModal = ({ isOpen, onClose }) => {
    const { user, logout, userProfile, refreshUserProfile, getToken } = useAuth();
    const navigate = useNavigate();
    const { theme, toggleTheme } = useTheme();
    const { showSuccess, showError } = useToast();
    const [emailSubscription, setEmailSubscription] = useState(true);
    const [creditData, setCreditData] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    // Load email preference from userProfile when modal opens
    useEffect(() => {
        if (isOpen) {
            fetchCredits();
            // Refresh user profile to get latest email_notifications setting
            refreshUserProfile();
        }
    }, [isOpen, refreshUserProfile]);

    // Sync email subscription whenever userProfile changes
    useEffect(() => {
        if (userProfile?.email_notifications !== undefined) {
            setEmailSubscription(userProfile.email_notifications);
        }
    }, [userProfile?.email_notifications]);

    const fetchCredits = async () => {
        try {
            const data = await apiEndpoints.auth.getCredits();
            setCreditData(data);
        } catch (error) {
            console.error('Error fetching credits:', error);
        }
    };

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Error logging out:', error);
            alert('Failed to logout. Please try again.');
        }
    };

    const handleUpgrade = () => {
        onClose();
        navigate('/upgrade');
    };

    const handleManageSubscription = async () => {
        try {
            const returnUrl = window.location.origin + '/dashboard';
            const { url } = await apiEndpoints.stripe.createPortalSession(returnUrl);
            window.location.href = url;
        } catch (error) {
            console.error('Error opening customer portal:', error);
            alert('Failed to open subscription management. Please try again.');
        }
    };

    const getDisplayName = () => {
        // First check userProfile (from database/onboarding), then OAuth metadata
        if (userProfile?.full_name) {
            return userProfile.full_name;
        }
        if (user?.user_metadata?.full_name) {
            return user.user_metadata.full_name;
        }
        const email = user?.email || '';
        return email.split('@')[0];
    };

    const getInitials = () => {
        const name = getDisplayName();
        return name.charAt(0).toUpperCase();
    };

    const getTierDisplay = () => {
        if (!creditData) return { label: 'Loading...', description: 'Please wait...' };

        switch (creditData.tier) {
            case 'premium':
                return {
                    label: 'Pro',
                    description: 'Unlimited access to all features',
                };
            case 'trial':
                return {
                    label: 'Trial',
                    description: 'Basic access with limited features',
                };
            case 'free':
                return {
                    label: 'Free',
                    description: 'Limited access - upgrade for more',
                };
            default:
                return { label: 'Unknown', description: '' };
        }
    };

    if (!isOpen) return null;

    const tierDisplay = getTierDisplay();

    const styles = {
        backdrop: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
        },
        modal: {
            width: '460px',
            maxHeight: '85vh',
            overflowY: 'auto',
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-light)',
            borderRadius: 'var(--radius-xl)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
            zIndex: 10000,
        },
        header: {
            padding: 'var(--space-xl)',
            borderBottom: '1px solid var(--border-subtle)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        title: {
            fontFamily: 'var(--font-display)',
            fontSize: '1.25rem',
            fontWeight: 600,
            color: 'var(--text-primary)',
            margin: 0,
        },
        closeBtn: {
            background: 'transparent',
            border: 'none',
            color: 'var(--text-tertiary)',
            fontSize: '24px',
            cursor: 'pointer',
            padding: 'var(--space-xs)',
            lineHeight: 1,
            borderRadius: 'var(--radius-sm)',
            transition: 'all var(--duration-fast)',
        },
        section: {
            padding: 'var(--space-xl)',
            borderBottom: '1px solid var(--border-subtle)',
        },
        sectionLast: {
            padding: 'var(--space-xl)',
        },
        sectionLabel: {
            fontSize: '0.75rem',
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            marginBottom: 'var(--space-md)',
            fontWeight: 600,
        },
        sectionDesc: {
            fontSize: '0.85rem',
            color: 'var(--text-tertiary)',
            marginBottom: 'var(--space-lg)',
        },
        card: {
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-lg)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-md)',
        },
        avatar: {
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            background: 'var(--gradient-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '1.1rem',
            fontWeight: 600,
            flexShrink: 0,
        },
        userInfo: {
            flex: 1,
            minWidth: 0,
        },
        userName: {
            fontWeight: 600,
            color: 'var(--text-primary)',
            marginBottom: '2px',
        },
        userEmail: {
            fontSize: '0.85rem',
            color: 'var(--text-tertiary)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
        logoutBtn: {
            padding: 'var(--space-sm) var(--space-md)',
            background: 'rgba(239, 68, 68, 0.1)',
            color: 'var(--red-500)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: 600,
            transition: 'all var(--duration-fast)',
            whiteSpace: 'nowrap',
        },
        planIcon: {
            width: '48px',
            height: '48px',
            borderRadius: 'var(--radius-md)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.5rem',
            flexShrink: 0,
        },
        planInfo: {
            flex: 1,
            minWidth: 0,
        },
        planName: {
            fontWeight: 600,
            color: 'var(--text-primary)',
            marginBottom: '2px',
        },
        planDesc: {
            fontSize: '0.85rem',
            color: 'var(--text-tertiary)',
        },
        upgradeBtn: {
            padding: 'var(--space-sm) var(--space-md)',
            background: 'var(--gradient-primary)',
            color: 'white',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: 600,
            transition: 'all var(--duration-fast)',
            whiteSpace: 'nowrap',
        },
        manageBtn: {
            padding: 'var(--space-sm) var(--space-md)',
            background: 'var(--bg-surface)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-light)',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            fontSize: '0.85rem',
            fontWeight: 600,
            transition: 'all var(--duration-fast)',
            whiteSpace: 'nowrap',
        },
        proNote: {
            marginTop: 'var(--space-md)',
            padding: 'var(--space-md)',
            background: 'rgba(34, 197, 94, 0.05)',
            border: '1px solid rgba(34, 197, 94, 0.2)',
            borderRadius: 'var(--radius-md)',
            fontSize: '0.8rem',
            color: 'var(--text-tertiary)',
        },
        toggleRow: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        toggleLabel: {
            fontWeight: 500,
            color: 'var(--text-primary)',
            marginBottom: '2px',
        },
        toggleDesc: {
            fontSize: '0.85rem',
            color: 'var(--text-tertiary)',
        },
        toggle: {
            position: 'relative',
            display: 'inline-block',
            width: '50px',
            height: '28px',
            cursor: 'pointer',
        },
        toggleInput: {
            display: 'none',
        },
        toggleTrack: (isOn) => ({
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: isOn ? 'var(--indigo-500)' : 'var(--bg-surface)',
            border: isOn ? 'none' : '1px solid var(--border-light)',
            borderRadius: 'var(--radius-full)',
            transition: 'background var(--duration-base)',
        }),
        toggleThumb: (isOn) => ({
            position: 'absolute',
            height: '20px',
            width: '20px',
            left: isOn ? '26px' : '4px',
            bottom: '4px',
            background: 'white',
            borderRadius: '50%',
            transition: 'left var(--duration-base)',
            boxShadow: 'var(--shadow-sm)',
        }),
        saveBtn: {
            width: '100%',
            padding: 'var(--space-md)',
            background: 'var(--gradient-primary)',
            color: 'white',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            cursor: 'pointer',
            fontSize: '0.95rem',
            fontWeight: 600,
            marginTop: 'var(--space-lg)',
            transition: 'all var(--duration-fast)',
        },
    };

    return createPortal(
        <div style={styles.backdrop} onClick={onClose}>
            {/* Modal */}
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div style={styles.header}>
                    <h2 style={styles.title}>Account Settings</h2>
                    <button
                        onClick={onClose}
                        style={styles.closeBtn}
                        onMouseOver={(e) => {
                            e.currentTarget.style.background = 'var(--bg-surface)';
                            e.currentTarget.style.color = 'var(--text-primary)';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.color = 'var(--text-tertiary)';
                        }}
                    >
                        ×
                    </button>
                </div>

                {/* Account Information */}
                <div style={styles.section}>
                    <div style={styles.sectionLabel}>Account Information</div>
                    <div style={styles.sectionDesc}>Your account details and authentication</div>

                    <div style={styles.card}>
                        <div style={styles.avatar}>{getInitials()}</div>
                        <div style={styles.userInfo}>
                            <div style={styles.userName}>{getDisplayName()}</div>
                            <div style={styles.userEmail}>{user?.email}</div>
                        </div>
                        <button
                            onClick={handleLogout}
                            style={styles.logoutBtn}
                            onMouseOver={(e) => {
                                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                            }}
                        >
                            Log Out
                        </button>
                    </div>
                </div>

                {/* Plan Management */}
                <div style={styles.section}>
                    <div style={styles.sectionLabel}>Plan Management</div>
                    <div style={styles.sectionDesc}>Manage your subscription and billing</div>

                    <div style={styles.card}>
                        <div
                            style={{
                                ...styles.planIcon,
                                background: creditData?.tier === 'premium'
                                    ? 'linear-gradient(135deg, var(--amber-500) 0%, var(--amber-400) 100%)'
                                    : 'var(--bg-surface)',
                                border: creditData?.tier === 'premium'
                                    ? 'none'
                                    : '1px solid var(--border-light)',
                                color: creditData?.tier === 'premium' ? '#000' : 'var(--text-secondary)',
                            }}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '24px', height: '24px' }}>
                                {creditData?.tier === 'premium' ? (
                                    <path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7zM3 20h18"/>
                                ) : (
                                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                                )}
                            </svg>
                        </div>
                        <div style={styles.planInfo}>
                            <div style={styles.planName}>{tierDisplay.label}</div>
                            <div style={styles.planDesc}>{tierDisplay.description}</div>
                        </div>

                        {creditData?.tier === 'premium' ? (
                            <button
                                onClick={handleManageSubscription}
                                style={styles.manageBtn}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.background = 'var(--bg-elevated)';
                                    e.currentTarget.style.borderColor = 'var(--border-medium)';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.background = 'var(--bg-surface)';
                                    e.currentTarget.style.borderColor = 'var(--border-light)';
                                }}
                            >
                                Manage
                            </button>
                        ) : (
                            <button
                                onClick={handleUpgrade}
                                style={styles.upgradeBtn}
                                onMouseOver={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                    e.currentTarget.style.boxShadow = 'var(--glow-indigo)';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                Upgrade
                            </button>
                        )}
                    </div>

                    {creditData?.tier === 'premium' && (
                        <div style={styles.proNote}>
                            Click "Manage" to update billing, view invoices, or cancel your subscription through Stripe's secure portal.
                        </div>
                    )}
                </div>

                {/* Preferences */}
                <div style={styles.sectionLast}>
                    <div style={styles.sectionLabel}>Preferences</div>
                    <div style={styles.sectionDesc}>Customize your app experience</div>

                    {/* Theme Toggle */}
                    <div style={{ ...styles.card, marginBottom: 'var(--space-md)' }}>
                        <div style={styles.toggleRow}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: 'var(--radius-md)',
                                    background: theme === 'dark' ? 'var(--bg-deep)' : 'var(--bg-surface)',
                                    border: '1px solid var(--border-subtle)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: theme === 'dark' ? 'var(--amber-400)' : 'var(--indigo-500)',
                                }}>
                                    {theme === 'dark' ? (
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '20px', height: '20px' }}>
                                            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                                        </svg>
                                    ) : (
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '20px', height: '20px' }}>
                                            <circle cx="12" cy="12" r="5"/>
                                            <line x1="12" y1="1" x2="12" y2="3"/>
                                            <line x1="12" y1="21" x2="12" y2="23"/>
                                            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
                                            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                                            <line x1="1" y1="12" x2="3" y2="12"/>
                                            <line x1="21" y1="12" x2="23" y2="12"/>
                                            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
                                            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                                        </svg>
                                    )}
                                </div>
                                <div>
                                    <div style={styles.toggleLabel}>Dark Mode</div>
                                    <div style={styles.toggleDesc}>{theme === 'dark' ? 'Currently using dark theme' : 'Currently using light theme'}</div>
                                </div>
                            </div>

                            <label style={styles.toggle}>
                                <input
                                    type="checkbox"
                                    checked={theme === 'dark'}
                                    onChange={toggleTheme}
                                    style={styles.toggleInput}
                                />
                                <div style={styles.toggleTrack(theme === 'dark')}>
                                    <div style={styles.toggleThumb(theme === 'dark')} />
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Email Notifications Toggle */}
                    <div style={styles.card}>
                        <div style={styles.toggleRow}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)' }}>
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: 'var(--radius-md)',
                                    background: 'var(--bg-surface)',
                                    border: '1px solid var(--border-subtle)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'var(--text-secondary)',
                                }}>
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: '20px', height: '20px' }}>
                                        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                                        <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                                    </svg>
                                </div>
                                <div>
                                    <div style={styles.toggleLabel}>Email Notifications</div>
                                    <div style={styles.toggleDesc}>Receive updates from Careerly</div>
                                </div>
                            </div>

                            <label style={styles.toggle}>
                                <input
                                    type="checkbox"
                                    checked={emailSubscription}
                                    onChange={(e) => setEmailSubscription(e.target.checked)}
                                    style={styles.toggleInput}
                                />
                                <div style={styles.toggleTrack(emailSubscription)}>
                                    <div style={styles.toggleThumb(emailSubscription)} />
                                </div>
                            </label>
                        </div>
                    </div>

                    <button
                        style={{
                            ...styles.saveBtn,
                            opacity: isSaving ? 0.7 : 1,
                            cursor: isSaving ? 'wait' : 'pointer',
                        }}
                        disabled={isSaving}
                        onClick={async () => {
                            setIsSaving(true);
                            try {
                                const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
                                const token = getToken();
                                const response = await fetch(`${API_URL}/auth/user/preferences`, {
                                    method: 'PATCH',
                                    headers: {
                                        'Authorization': `Bearer ${token}`,
                                        'Content-Type': 'application/json',
                                    },
                                    body: JSON.stringify({
                                        email_notifications: emailSubscription,
                                        theme_preference: theme,
                                    }),
                                });

                                if (response.ok) {
                                    // Refresh user profile to sync with context
                                    refreshUserProfile();
                                    showSuccess('Settings saved successfully!');
                                } else {
                                    const error = await response.json();
                                    showError(error.detail || 'Failed to save settings');
                                }
                            } catch (err) {
                                console.error('Error saving preferences:', err);
                                showError('Failed to save settings. Please try again.');
                            } finally {
                                setIsSaving(false);
                            }
                        }}
                        onMouseOver={(e) => {
                            if (!isSaving) {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = 'var(--glow-indigo)';
                            }
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                        }}
                    >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default AccountSettingsModal;
